import React from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Card,
  CardContent,
  Avatar,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  useTheme,
  alpha,
  Chip,
} from "@mui/material";
import {
  Groups as GroupsIcon,
  Payments as PaymentsIcon,
  AccountBalance as AccountBalanceIcon,
  Repeat as RepeatIcon,
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  ArrowForward as ArrowForwardIcon,
  Celebration as CelebrationIcon,
  TrendingUp as TrendingUpIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Handshake as HandshakeIcon,
  EmojiEvents as TrophyIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

// Circle visualization component
const CircleVisualization: React.FC<{
  members: { name: string; isPaying: boolean; isReceiving: boolean }[];
  month: number;
}> = ({ members, month }) => {
  const theme = useTheme();
  const totalMembers = members.length;
  const radius = 120;
  const centerX = 150;
  const centerY = 150;

  return (
    <Box sx={{ position: "relative", width: 300, height: 300, mx: "auto" }}>
      <svg width="300" height="300" viewBox="0 0 300 300">
        {/* Center circle */}
        <circle
          cx={centerX}
          cy={centerY}
          r={40}
          fill={alpha(theme.palette.primary.main, 0.1)}
          stroke={theme.palette.primary.main}
          strokeWidth={2}
        />
        <text
          x={centerX}
          y={centerY - 8}
          textAnchor="middle"
          fill={theme.palette.primary.main}
          fontSize="12"
          fontWeight="bold"
        >
          Month {month}
        </text>
        <text
          x={centerX}
          y={centerY + 8}
          textAnchor="middle"
          fill={theme.palette.primary.main}
          fontSize="10"
        >
          Pool: $2,000
        </text>

        {/* Member circles positioned around */}
        {members.map((member, index) => {
          const angle = (index / totalMembers) * 2 * Math.PI - Math.PI / 2;
          const x = centerX + radius * Math.cos(angle);
          const y = centerY + radius * Math.sin(angle);

          return (
            <g key={index}>
              {/* Line to center */}
              <line
                x1={centerX}
                y1={centerY}
                x2={x}
                y2={y}
                stroke={
                  member.isReceiving
                    ? theme.palette.success.main
                    : member.isPaying
                      ? theme.palette.primary.main
                      : theme.palette.grey[300]
                }
                strokeWidth={member.isReceiving ? 3 : 1}
                strokeDasharray={member.isPaying ? "none" : "5,5"}
              />
              {/* Member circle */}
              <circle
                cx={x}
                cy={y}
                r={25}
                fill={
                  member.isReceiving
                    ? theme.palette.success.main
                    : member.isPaying
                      ? theme.palette.primary.main
                      : theme.palette.grey[200]
                }
                stroke={
                  member.isReceiving
                    ? theme.palette.success.dark
                    : member.isPaying
                      ? theme.palette.primary.dark
                      : theme.palette.grey[400]
                }
                strokeWidth={2}
              />
              <text
                x={x}
                y={y + 4}
                textAnchor="middle"
                fill={member.isPaying || member.isReceiving ? "white" : "#666"}
                fontSize="10"
                fontWeight="bold"
              >
                {member.name}
              </text>
            </g>
          );
        })}
      </svg>

      {/* Legend */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: 2,
          mt: 1,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              bgcolor: "primary.main",
            }}
          />
          <Typography variant="caption">Contributing</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              bgcolor: "success.main",
            }}
          />
          <Typography variant="caption">Receiving Payout</Typography>
        </Box>
      </Box>
    </Box>
  );
};

// Timeline card for showing rotation
const RotationCard: React.FC<{
  month: number;
  recipient: string;
  amount: string;
  isActive?: boolean;
}> = ({ month, recipient, amount, isActive }) => {
  const theme = useTheme();

  return (
    <Card
      elevation={isActive ? 4 : 1}
      sx={{
        minWidth: 140,
        border: isActive ? `2px solid ${theme.palette.primary.main}` : "none",
        bgcolor: isActive
          ? alpha(theme.palette.primary.main, 0.05)
          : "background.paper",
        transition: "all 0.3s",
      }}
    >
      <CardContent sx={{ textAlign: "center", py: 2 }}>
        <Chip
          label={`Month ${month}`}
          size="small"
          color={isActive ? "primary" : "default"}
          sx={{ mb: 1 }}
        />
        <Avatar
          sx={{
            mx: "auto",
            mb: 1,
            bgcolor: isActive ? "primary.main" : "grey.300",
            width: 36,
            height: 36,
          }}
        >
          <PersonIcon fontSize="small" />
        </Avatar>
        <Typography variant="body2" fontWeight={600}>
          {recipient}
        </Typography>
        <Typography variant="h6" color="success.main" fontWeight={700}>
          {amount}
        </Typography>
      </CardContent>
    </Card>
  );
};

const HowItWorksPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const steps = [
    {
      label: "Join or Create a Circle",
      description:
        "Find a circle that matches your savings goal, or create your own and invite trusted friends, family, or community members. Each circle has a set contribution amount and schedule.",
      icon: <GroupsIcon />,
    },
    {
      label: "Everyone Contributes",
      description:
        "Each month (or chosen period), all members contribute the same fixed amount to a shared pool. For example, if 5 members each contribute $500, the pool is $2,500.",
      icon: <PaymentsIcon />,
    },
    {
      label: "One Member Receives the Pool",
      description:
        "Each period, one member receives the entire pool. The order is determined when the circle starts — either chosen by preference, random, or by agreement.",
      icon: <AccountBalanceIcon />,
    },
    {
      label: "Rotation Continues",
      description:
        "The process repeats each period with a different member receiving the pool, until everyone has had their turn. Then the circle is complete!",
      icon: <RepeatIcon />,
    },
    {
      label: "Circle Complete",
      description:
        "Once all members have received their payout, the circle ends. You can then join or create a new circle to continue building your savings habits.",
      icon: <CheckCircleIcon />,
    },
  ];

  const exampleMembers = [
    { name: "Sarah", isPaying: true, isReceiving: false },
    { name: "Mike", isPaying: true, isReceiving: false },
    { name: "Lisa", isPaying: false, isReceiving: true },
    { name: "John", isPaying: true, isReceiving: false },
    { name: "Emma", isPaying: true, isReceiving: false },
  ];

  const benefits = [
    {
      icon: <TrendingUpIcon />,
      title: "Forced Savings",
      description:
        "The commitment to your group motivates consistent saving habits.",
    },
    {
      icon: <SpeedIcon />,
      title: "Access Larger Sums",
      description:
        "Get access to a lump sum faster than saving alone — great for big purchases.",
    },
    {
      icon: <SecurityIcon />,
      title: "No Interest or Fees",
      description:
        "Unlike loans, there's no interest. You contribute and receive the same total amount.",
    },
    {
      icon: <HandshakeIcon />,
      title: "Community Trust",
      description:
        "Build financial trust with friends, family, or community members.",
    },
  ];

  const faqs = [
    {
      question: "What if someone doesn't pay?",
      answer:
        "Sanduuq has safeguards in place. Members are verified before joining, and we track payment history. If a member misses a payment, they face penalties and may be banned from future circles. However, there's always some risk in peer-to-peer savings, which is why we recommend participating with trusted individuals.",
    },
    {
      question: "How is the payout order determined?",
      answer:
        "The circle administrator can set the payout order when creating the circle. Options include: first-come-first-served based on join order, random selection, or mutual agreement among members. Once the circle starts, the order is locked.",
    },
    {
      question: "Can I leave a circle early?",
      answer:
        "Once a circle becomes active, you're committed to completing all payments. This is essential to maintain trust and fairness for all members. Leaving early without fulfilling your obligations will result in penalties and account restrictions.",
    },
    {
      question: "Is my money safe?",
      answer:
        "Payments are processed securely through Stripe. However, Sanduuq is a platform that facilitates peer-to-peer savings circles — we don't hold or guarantee funds. The success of each circle depends on all members fulfilling their commitments.",
    },
    {
      question: "What's the minimum/maximum circle size?",
      answer:
        "Circles can have between 2 and 12 members. Smaller circles complete faster but provide smaller lump sums. Larger circles take longer but offer bigger payouts.",
    },
    {
      question: "How often are contributions made?",
      answer:
        "Most circles operate on a monthly basis, but the administrator can set weekly or bi-weekly schedules when creating the circle.",
    },
  ];

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
      {/* Hero Section */}
      <Box
        sx={{
          color: "white",
          py: 10,
          textAlign: "center",
          backgroundImage: "url('/leaves-removebg-preview.png')",
          backgroundColor: theme.palette.primary.main,
          backgroundBlendMode: "soft-light",
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h2"
            component="h1"
            fontWeight={800}
            gutterBottom
            sx={{ fontSize: { xs: "2.5rem", md: "3.5rem" } }}
          >
            How Sanduuq Works
          </Typography>
          <Typography
            variant="h5"
            sx={{ opacity: 0.9, maxWidth: 600, mx: "auto" }}
          >
            A modern take on the ancient tradition of community savings circles
          </Typography>
        </Container>
      </Box>

      {/* What is a Lending Circle Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography
              variant="overline"
              color="primary"
              fontWeight={600}
              gutterBottom
            >
              The Basics
            </Typography>
            <Typography variant="h3" fontWeight={700} gutterBottom>
              What is a Lending Circle?
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: "1.1rem" }}>
              A lending circle (also called a ROSCA, tanda, chit fund, or
              sou-sou) is a time-tested savings method used by communities
              around the world for centuries.
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: "1.1rem" }}>
              <strong>Here's the simple idea:</strong> A group of people agree
              to contribute a fixed amount regularly. Each period, one member
              receives the entire pool. This rotates until everyone has had
              their turn.
            </Typography>
            <Typography variant="body1" sx={{ fontSize: "1.1rem" }}>
              It's like a savings club where everyone wins — you contribute the
              same total amount you receive, but you get access to a lump sum
              exactly when you need it.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={3}
              sx={{
                p: 4,
                borderRadius: 4,
                bgcolor: alpha(theme.palette.primary.main, 0.02),
              }}
            >
              <Typography
                variant="h6"
                fontWeight={600}
                textAlign="center"
                gutterBottom
              >
                Example: 5-Member Circle
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
                sx={{ mb: 3 }}
              >
                Each member contributes $500/month
              </Typography>
              <CircleVisualization members={exampleMembers} month={3} />
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
                sx={{ mt: 5 }}
              >
                In Month 3, Lisa receives the $2,000 pool while everyone
                contributes
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Step by Step Process */}
      <Box sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03), py: 8 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            fontWeight={700}
            textAlign="center"
            gutterBottom
          >
            Step-by-Step Process
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            textAlign="center"
            sx={{ mb: 6, maxWidth: 600, mx: "auto" }}
          >
            From joining a circle to receiving your payout, here's exactly how
            it works
          </Typography>

          <Stepper orientation="vertical" sx={{ maxWidth: 700, mx: "auto" }}>
            {steps.map((step, index) => (
              <Step key={index} active expanded>
                <StepLabel
                  StepIconComponent={() => (
                    <Avatar
                      sx={{
                        bgcolor: "primary.main",
                        width: 40,
                        height: 40,
                      }}
                    >
                      {step.icon}
                    </Avatar>
                  )}
                >
                  <Typography variant="h6" fontWeight={600}>
                    {step.label}
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Typography variant="body1" color="text.secondary">
                    {step.description}
                  </Typography>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </Container>
      </Box>

      {/* Rotation Timeline */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h3"
          fontWeight={700}
          textAlign="center"
          gutterBottom
        >
          The Payout Rotation
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          textAlign="center"
          sx={{ mb: 6, maxWidth: 600, mx: "auto" }}
        >
          See how a 5-member circle with $500 monthly contributions works over
          time
        </Typography>

        <Box
          sx={{
            display: "flex",
            overflowX: "auto",
            gap: 2,
            pb: 2,
            justifyContent: { xs: "flex-start", md: "center" },
          }}
        >
          <RotationCard month={1} recipient="Sarah" amount="$2,000" />
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <ArrowForwardIcon color="action" />
          </Box>
          <RotationCard month={2} recipient="Mike" amount="$2,000" />
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <ArrowForwardIcon color="action" />
          </Box>
          <RotationCard month={3} recipient="Lisa" amount="$2,000" isActive />
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <ArrowForwardIcon color="action" />
          </Box>
          <RotationCard month={4} recipient="John" amount="$2,000" />
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <ArrowForwardIcon color="action" />
          </Box>
          <RotationCard month={5} recipient="Emma" amount="$2,000" />
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <CelebrationIcon color="success" />
          </Box>
        </Box>

        <Paper
          sx={{
            p: 3,
            mt: 4,
            borderRadius: 3,
            bgcolor: alpha(theme.palette.success.main, 0.05),
            border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
            maxWidth: 600,
            mx: "auto",
          }}
        >
          <Typography variant="h6" fontWeight={600} gutterBottom>
            📊 The Math
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Your total contributions:
              </Typography>
              <Typography variant="h5" fontWeight={700}>
                $500 × 5 = $2,500
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Your payout:
              </Typography>
              <Typography variant="h5" fontWeight={700} color="success.main">
                $2,000*
              </Typography>
            </Grid>
          </Grid>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: "block" }}
          >
            *Small platform fee may apply. You contribute the same total as you
            receive.
          </Typography>
        </Paper>
      </Container>

      {/* Benefits Section */}
      <Box sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03), py: 8 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            fontWeight={700}
            textAlign="center"
            gutterBottom
          >
            Why Join a Circle?
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            textAlign="center"
            sx={{ mb: 6, maxWidth: 600, mx: "auto" }}
          >
            Discover the benefits of community-based savings
          </Typography>

          <Grid container spacing={4}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    textAlign: "center",
                    p: 3,
                    border: `1px solid ${theme.palette.divider}`,
                    transition: "all 0.3s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: theme.shadows[4],
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      mx: "auto",
                      mb: 2,
                      width: 60,
                      height: 60,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: "primary.main",
                    }}
                  >
                    {benefit.icon}
                  </Avatar>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    {benefit.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {benefit.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Typography
          variant="h3"
          fontWeight={700}
          textAlign="center"
          gutterBottom
        >
          Frequently Asked Questions
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          textAlign="center"
          sx={{ mb: 6 }}
        >
          Got questions? We've got answers.
        </Typography>

        {faqs.map((faq, index) => (
          <Accordion
            key={index}
            elevation={0}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              mb: 1,
              "&:before": { display: "none" },
              borderRadius: "8px !important",
              overflow: "hidden",
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={600}>{faq.question}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color="text.secondary">{faq.answer}</Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Container>

      {/* CTA Section */}
      <Box
        sx={{
          color: "white",
          py: 4,
          textAlign: "center",
          backgroundImage: "url('/leaves-removebg-preview.png')",
          backgroundColor: theme.palette.primary.main,
          backgroundBlendMode: "soft-light",
        }}
      >
        <Container maxWidth="sm">
          <TrophyIcon sx={{ fontSize: 60, mb: 2, opacity: 0.9 }} />
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Ready to Start Saving?
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9, mb: 4 }}>
            Join thousands of members who are reaching their financial goals
            through community savings.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate("/register")}
            sx={{
              bgcolor: "white",
              color: "primary.main",
              px: 4,
              py: 1.5,
              fontWeight: 600,
              "&:hover": {
                bgcolor: alpha("#fff", 0.9),
              },
            }}
          >
            Get Started Today
          </Button>
        </Container>
      </Box>
    </Box>
  );
};

export default HowItWorksPage;
